package features.overtime.data

import core.firebase.FirebaseService
import features.overtime.domain.OvertimeRequest
import kotlin.js.Promise
import kotlin.js.json
import kotlinx.browser.window

/**
 * Repository for managing overtime requests in Firestore
 */
object OvertimeRepository {
    
    /**
     * Create a new overtime request
     * @param teamId Team ID
     * @param userId User ID who is requesting overtime
     * @param date Date of overtime in YYYY-MM-DD format
     * @param startTime Start time in HH:mm format
     * @param endTime End time in HH:mm format
     * @param reason Reason for overtime
     * @return Promise with the created request ID
     */
    fun createOvertimeRequest(
        teamId: String,
        userId: String,
        date: String,
        startTime: String,
        endTime: String,
        reason: String
    ): Promise<String> {
        return Promise { resolve, reject ->
            try {
                // Calculate hours
                val hours = calculateHours(startTime, endTime)
                
                val overtimeData = json(
                    "userId" to userId,
                    "date" to date,
                    "startTime" to startTime,
                    "endTime" to endTime,
                    "hours" to hours,
                    "reason" to reason,
                    "status" to "Pending",
                    "createdAt" to js("firebase.firestore.FieldValue.serverTimestamp()"),
                    "reviewedAt" to null,
                    "reviewedBy" to null
                )
                
                FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("overtime")
                    .add(overtimeData)
                    .then { docRef ->
                        console.log("✅ Overtime request created, ID:", docRef.id)
                        resolve(docRef.id)
                    }
                    .catch { error ->
                        console.error("❌ Create overtime request failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Create overtime request error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Get overtime requests for a team with optional filters
     * @param teamId Team ID
     * @param userId Optional user ID filter
     * @param status Optional status filter ("Pending", "Approved", "Rejected")
     * @return Promise with list of overtime requests
     */
    fun getOvertimeRequests(
        teamId: String,
        userId: String? = null,
        status: String? = null
    ): Promise<List<OvertimeRequest>> {
        return Promise { resolve, reject ->
            try {
                var query: dynamic = FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("overtime")
                
                // Apply filters
                if (userId != null) {
                    query = query.where("userId", "==", userId)
                }
                if (status != null) {
                    query = query.where("status", "==", status)
                }
                
                // Order by creation date descending
                query = query.orderBy("createdAt", "desc")
                
                query.get()
                    .then { snapshot: dynamic ->
                        val requests = mutableListOf<OvertimeRequest>()
                        snapshot.forEach { doc: dynamic ->
                            try {
                                val rawData: dynamic = doc.data()
                                
                                val userId = rawData.userId as? String ?: ""
                                val date = rawData.date as? String ?: ""
                                val startTime = rawData.startTime as? String ?: ""
                                val endTime = rawData.endTime as? String ?: ""
                                val hours = rawData.hours as? String ?: ""
                                val reason = rawData.reason as? String ?: ""
                                val status = rawData.status as? String ?: "Pending"
                                
                                // Handle timestamps
                                val createdAt = formatTimestamp(rawData.createdAt)
                                val reviewedAt = if (rawData.reviewedAt != null) {
                                    formatTimestamp(rawData.reviewedAt)
                                } else null
                                val reviewedBy = rawData.reviewedBy as? String
                                
                                val request = OvertimeRequest(
                                    id = doc.id,
                                    userId = userId,
                                    date = date,
                                    startTime = startTime,
                                    endTime = endTime,
                                    hours = hours,
                                    reason = reason,
                                    status = status,
                                    createdAt = createdAt,
                                    reviewedAt = reviewedAt,
                                    reviewedBy = reviewedBy
                                )
                                requests.add(request)
                            } catch (e: Throwable) {
                                console.error("❌ Error processing overtime doc:", e)
                            }
                        }
                        console.log("✅ Loaded ${requests.size} overtime requests")
                        resolve(requests)
                    }
                    .catch { error: dynamic ->
                        console.error("❌ Get overtime requests failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Get overtime requests error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Get a single overtime request by ID
     * @param teamId Team ID
     * @param requestId Request ID
     * @return Promise with the overtime request or null if not found
     */
    fun getOvertimeRequestById(teamId: String, requestId: String): Promise<OvertimeRequest?> {
        return Promise { resolve, reject ->
            try {
                FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("overtime")
                    .doc(requestId)
                    .get()
                    .then { doc: dynamic ->
                        if (doc.exists) {
                            val rawData: dynamic = doc.data()
                            
                            val request = OvertimeRequest(
                                id = doc.id,
                                userId = rawData.userId as? String ?: "",
                                date = rawData.date as? String ?: "",
                                startTime = rawData.startTime as? String ?: "",
                                endTime = rawData.endTime as? String ?: "",
                                hours = rawData.hours as? String ?: "",
                                reason = rawData.reason as? String ?: "",
                                status = rawData.status as? String ?: "Pending",
                                createdAt = formatTimestamp(rawData.createdAt),
                                reviewedAt = if (rawData.reviewedAt != null) {
                                    formatTimestamp(rawData.reviewedAt)
                                } else null,
                                reviewedBy = rawData.reviewedBy as? String
                            )
                            resolve(request)
                        } else {
                            resolve(null)
                        }
                    }
                    .catch { error: dynamic ->
                        console.error("❌ Get overtime request failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Get overtime request error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Approve an overtime request
     * @param teamId Team ID
     * @param requestId Request ID
     * @param reviewerId User ID of the reviewer (admin/owner)
     * @return Promise
     */
    fun approveRequest(teamId: String, requestId: String, reviewerId: String): Promise<Unit> {
        return Promise { resolve, reject ->
            try {
                val updateData = json(
                    "status" to "Approved",
                    "reviewedAt" to js("firebase.firestore.FieldValue.serverTimestamp()"),
                    "reviewedBy" to reviewerId
                )
                
                FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("overtime")
                    .doc(requestId)
                    .update(updateData)
                    .then {
                        console.log("✅ Overtime request approved")
                        resolve(Unit)
                    }
                    .catch { error ->
                        console.error("❌ Approve overtime request failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Approve overtime request error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Reject an overtime request
     * @param teamId Team ID
     * @param requestId Request ID
     * @param reviewerId User ID of the reviewer (admin/owner)
     * @return Promise
     */
    fun rejectRequest(teamId: String, requestId: String, reviewerId: String): Promise<Unit> {
        return Promise { resolve, reject ->
            try {
                val updateData = json(
                    "status" to "Rejected",
                    "reviewedAt" to js("firebase.firestore.FieldValue.serverTimestamp()"),
                    "reviewedBy" to reviewerId
                )
                
                FirebaseService.db
                    .collection("teams")
                    .doc(teamId)
                    .collection("overtime")
                    .doc(requestId)
                    .update(updateData)
                    .then {
                        console.log("✅ Overtime request rejected")
                        resolve(Unit)
                    }
                    .catch { error ->
                        console.error("❌ Reject overtime request failed:", error)
                        reject(error)
                    }
            } catch (e: Throwable) {
                console.error("❌ Reject overtime request error:", e)
                reject(e)
            }
        }
    }
    
    /**
     * Calculate hours between start and end time
     * @param startTime Start time in HH:mm format
     * @param endTime End time in HH:mm format
     * @return Hours in "X.Xh" format
     */
    private fun calculateHours(startTime: String, endTime: String): String {
        try {
            val startParts = startTime.split(":")
            val endParts = endTime.split(":")
            
            val startHour = startParts[0].toInt()
            val startMinute = startParts[1].toInt()
            val endHour = endParts[0].toInt()
            val endMinute = endParts[1].toInt()
            
            var totalMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute)
            
            // Handle overnight shifts (e.g., 22:00 to 07:00)
            if (totalMinutes < 0) {
                totalMinutes += 24 * 60
            }
            
            val hours = totalMinutes / 60.0
            // Use JS toFixed() instead of String.format (not available in Kotlin/JS)
            return "${hours.asDynamic().toFixed(1)}h"
        } catch (e: Throwable) {
            console.error("❌ Calculate hours error:", e)
            return "0.0h"
        }
    }
    
    /**
     * Format Firestore timestamp to string
     * @param timestamp Firestore timestamp
     * @return Formatted date string
     */
    private fun formatTimestamp(timestamp: dynamic): String {
        return try {
            if (timestamp == null || timestamp == js("undefined")) {
                ""
            } else {
                // Convert Firestore timestamp to Date
                val date: dynamic = timestamp.toDate()
                date.toISOString()
            }
        } catch (e: Throwable) {
            ""
        }
    }
}
